import React, { useState } from 'react';
import { CheckIcon, GripVerticalIcon, PlusIcon, StarIcon, Trash2Icon } from 'lucide-react';
import { PipelineStage } from '../../types';
import { createStage, deleteStage, reorderStages, setInitialStage, updateStage } from '../../services/organizationService';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Switch } from '../ui/Switch';
import { ConfirmDialog } from './ConfirmDialog';
interface StageManagerProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  stages: PipelineStage[];
}

/** Paleta de colores disponible para los estados. */
export const STAGE_COLORS: {
  value: string;
  label: string;
}[] = [{
  value: '#64748B',
  label: 'Gris'
}, {
  value: '#3D8BFF',
  label: 'Azul'
}, {
  value: '#16A34A',
  label: 'Verde'
}, {
  value: '#DC2626',
  label: 'Rojo'
}, {
  value: '#EAB308',
  label: 'Amarillo'
}, {
  value: '#F97316',
  label: 'Naranja'
}, {
  value: '#7C5CFF',
  label: 'Morado'
}, {
  value: '#EC4899',
  label: 'Rosa'
}];

/**
 * Gestión completa de los estados del embudo: crear, renombrar, recolorear,
 * reordenar, activar, desactivar, eliminar y elegir el estado inicial.
 */
export function StageManager({
  open,
  onClose,
  organizationId,
  stages
}: StageManagerProps) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(STAGE_COLORS[1].value);
  const [creating, setCreating] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<PipelineStage | null>(null);
  const [deleting, setDeleting] = useState(false);
  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    await createStage(organizationId, newName.trim(), newColor);
    setNewName('');
    setCreating(false);
  }
  async function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) return;
    const ordered = stages.map(s => s.id);
    const from = ordered.indexOf(dragId);
    const to = ordered.indexOf(targetId);
    ordered.splice(to, 0, ordered.splice(from, 1)[0]);
    setDragId(null);
    await reorderStages(organizationId, ordered);
  }
  async function handleDelete() {
    if (!confirm) return;
    setDeleting(true);
    await deleteStage(confirm.id);
    setDeleting(false);
    setConfirm(null);
  }
  return <>
      <Drawer open={open} onClose={onClose} title="Gestionar estados" data-id="element-197" data-mp="vabdfyxtozee">
        <p className="mb-5 text-sm text-muted-foreground" data-id="element-198" data-mp="qciarfvqaniv">
          Los cambios se aplican al instante en la tabla de leads, la ficha del
          contacto, los filtros, el embudo y el dashboard.
        </p>

        <ul className="space-y-3" data-id="element-199" data-mp="gmyqc5sdallw">
          {stages.map(stage => <li key={stage.id} draggable onDragStart={() => setDragId(stage.id)} onDragOver={e => e.preventDefault()} onDrop={() => handleDrop(stage.id)} className={`rounded-xl border border-border bg-background p-3 ${dragId === stage.id ? 'opacity-60' : ''}`} data-id="element-200" data-mp="gkch81ptvy1k">
              <div className="flex items-center gap-2" data-id="element-201" data-mp="1gnbvkyc8kdy4">
                <GripVerticalIcon className="h-4 w-4 flex-none cursor-grab text-muted-foreground" aria-hidden="true" data-id="element-202" data-mp="1lldjbwklx1fx" />
                <Input id={`stage-${stage.id}`} aria-label="Nombre del estado" value={stage.name} onChange={e => updateStage(stage.id, {
              name: e.target.value
            })} data-id="element-203" data-mp="1qjx3psjjy566" />
                <button type="button" aria-label={stage.isInitial ? 'Estado inicial' : 'Marcar como estado inicial'} onClick={() => setInitialStage(organizationId, stage.id)} className={`rounded-md p-2 ${stage.isInitial ? 'text-amber-500' : 'text-muted-foreground hover:bg-surface'}`} data-id="element-204" data-mp="1zleeqwi3rgo0">
                  <StarIcon className="h-4 w-4" fill={stage.isInitial ? 'currentColor' : 'none'} data-id="element-205" data-mp="2ea15rvh3dwv2" />
                </button>
                <button type="button" aria-label="Eliminar estado" disabled={stages.length <= 1} onClick={() => setConfirm(stage)} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600 disabled:opacity-40" data-id="element-206" data-mp="2j9ni82p3j92t">
                  <Trash2Icon className="h-4 w-4" data-id="element-207" data-mp="3ky0vllixloe1" />
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 pl-6" data-id="element-208" data-mp="3fzzf98cw4369">
                <div className="flex flex-wrap gap-1.5" data-id="element-209" data-mp="3tqm6kfgbmqsb">
                  {STAGE_COLORS.map(color => <button key={color.value} type="button" aria-label={color.label} aria-pressed={stage.color === color.value} onClick={() => updateStage(stage.id, {
                color: color.value
              })} className={`flex h-7 w-7 items-center justify-center rounded-full transition-transform hover:scale-110 ${stage.color === color.value ? 'ring-2 ring-foreground ring-offset-2' : ''}`} style={{
                backgroundColor: color.value
              }} data-id="element-210" data-mp="3prdc18dgiovp">
                      {stage.color === color.value && <CheckIcon className="h-3.5 w-3.5 text-white" data-id="element-211" data-mp="q9vsddal57fj" />}
                    </button>)}
                </div>
                <Switch id={`stage-active-${stage.id}`} label="Activo" checked={stage.active} onChange={active => updateStage(stage.id, {
              active
            })} data-id="element-212" data-mp="lbuc104jnm7r" />
              </div>
            </li>)}
        </ul>

        <div className="mt-6 rounded-xl border border-dashed border-border p-4" data-id="element-213" data-mp="juwy6frjv6kg">
          <Input id="new-stage" label="Nuevo estado" placeholder="Ejemplo: Segunda visita" value={newName} onChange={e => setNewName(e.target.value)} data-id="element-214" data-mp="jx1ddtuwfs01" />
          <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-215" data-mp="1cjcc0yyy3iu7">
            {STAGE_COLORS.map(color => <button key={color.value} type="button" aria-label={color.label} aria-pressed={newColor === color.value} onClick={() => setNewColor(color.value)} className={`h-7 w-7 rounded-full transition-transform hover:scale-110 ${newColor === color.value ? 'ring-2 ring-foreground ring-offset-2' : ''}`} style={{
            backgroundColor: color.value
          }} data-id="element-216" data-mp="1hj71qz6zi2do" />)}
          </div>
          <Button className="mt-4 w-full" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-218" data-mp="209yjop51f13o" />} loading={creating} disabled={!newName.trim()} onClick={handleCreate} data-id="element-217" data-mp="2096q1jv03dg1">
            Crear estado
          </Button>
        </div>
      </Drawer>

      <ConfirmDialog open={Boolean(confirm)} title={`Eliminar «${confirm?.name ?? ''}»`} description="Los leads que estén en este estado se moverán automáticamente al estado inicial. Esta acción no se puede deshacer." loading={deleting} onCancel={() => setConfirm(null)} onConfirm={handleDelete} data-id="element-219" data-mp="255sulfykm2lg" />
    </>;
}